import type { SignUpBody, UserInfo } from '../mutations/sign-up';
import type { SignUpFormValues } from '../schemas';

import { omitBy } from 'lodash';

import { BusinessStructure, BusinessType } from '@paid-ui/enums/business';
import { CompanyDirectorsStructure } from '@paid-ui/enums/user';

import { RoleInCompany, TrustType } from '../models/enums';
import { isIndividualSignUp } from './is-individual-sign-up';

const toOptional = (value: string | undefined): string | undefined => {
  return value?.trim() || undefined;
};

const provideBuildingServicesMap = {
  [BusinessType.OWNER]: false,
  [BusinessType.BUILDER]: true,
  [BusinessType.SUBCONTRACTOR]: true,
  [BusinessType.SUPPLIER]: true,
  [BusinessType.SUPERINTENDENT]: false,
  [BusinessType.FUNDER]: false,
  [BusinessType.QUANTITY_SURVEYOR]: false,
};

/**
 * Format sign up form values to the API request body
 *
 * @param formValues - Sign up form values
 */
export function formatFormValues(
  formValues: SignUpFormValues,
  options?: {
    withMainRole?: boolean;
  },
): SignUpBody | null {
  const {
    email,
    firstName,
    lastName,
    mobile,
    password,
    type: businessType,
    structure,
    abn,
    isEmployee = false,
  } = formValues;
  const userInfo = omitBy(
    {
      email,
      firstName,
      lastName,
      mobile: mobile.startsWith('61') ? `+${mobile}` : mobile,
      password,
      mainRole: options?.withMainRole ? businessType || undefined : undefined,
    },
    (v) => v === '' || v === undefined,
  ) as unknown as UserInfo;
  const optionalAbn = toOptional(abn);

  if (isIndividualSignUp(formValues)) {
    return userInfo;
  }

  const type = !businessType || businessType === BusinessType.OWNER ? null : businessType;
  const provideBuildingServices = type ? provideBuildingServicesMap[type] : false;

  switch (structure) {
    case BusinessStructure.COMPANY: {
      const { acn, companyName, roleInCompany } = formValues;
      const companyDirectorsStructure =
        roleInCompany === RoleInCompany.DIRECTOR
          ? CompanyDirectorsStructure.MULTI_DIRECTORS
          : CompanyDirectorsStructure.SOLE_DIRECTOR;
      return {
        ...userInfo,
        businessOverallInfo: {
          isEmployee,
          business: {
            provideBuildingServices,
            structure,
            abn: optionalAbn,
            acn,
            name: companyName,
            companyDirectorsStructure,
          },
        },
      };
    }

    case BusinessStructure.PARTNERSHIP:
    case BusinessStructure.SOLE_TRADER:
    case BusinessStructure.OTHER: {
      const { businessName } = formValues;
      return {
        ...userInfo,
        businessOverallInfo: {
          isEmployee,
          business: {
            provideBuildingServices,
            structure,
            abn: optionalAbn,
            name: businessName,
            companyDirectorsStructure: null,
          },
        },
      };
    }

    case BusinessStructure.TRUST: {
      const { acn, companyName, roleInCompany, trustName, trustType } = formValues;

      const trustInfo = {
        abn: optionalAbn,
        name: trustName,
        provideBuildingServices,
      };

      const companyDirectorsStructure =
        roleInCompany === RoleInCompany.DIRECTOR
          ? CompanyDirectorsStructure.MULTI_DIRECTORS
          : CompanyDirectorsStructure.SOLE_DIRECTOR;

      return [TrustType.OFFICER_OF_COMPANY, TrustType.EMPLOYEE_OF_COMPANY].includes(trustType)
        ? {
            ...userInfo,
            businessOverallInfo: {
              isEmployee,
              trustOverallInfo: {
                trust: trustInfo,
                companyTrustee: {
                  acn,
                  name: companyName,
                  companyDirectorsStructure,
                },
              },
            },
          }
        : {
            ...userInfo,
            businessOverallInfo: {
              isEmployee,
              trustOverallInfo: {
                trust: trustInfo,
                individualTrustee: {},
              },
            },
          };
    }

    default: {
      return userInfo;
    }
  }
}
